import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Trophy, Star } from 'lucide-react';

export const Awards = () => {
  const awards = [
    {
      id: 1,
      title: 'Appreciation of Valued Partnership',
      organization: 'AVAADA',
      event: 'Partners Meet 2025',
      year: '2025',
      description: 'Recognized for our valued partnership and outstanding contribution to AVAADA\'s renewable energy projects.',
      image: 'https://customer-assets-39nsmqrw.emergentagent.net/job_ed6e21f6-4588-40b2-8743-fbb7a90d8f8d/artifacts/682xhvgm_Avaada%20Partners%20Meet_Appreciation_191120255.webp',
      category: 'Partnership Recognition'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Awards & Recognition | Calspark Electrical & Engineers</title>
        <meta name="description" content="Discover the awards and recognition received by Calspark Electrical & Engineers for excellence in EPC contracting, renewable energy projects, and valued partnerships." />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 rounded-full px-4 py-2 mb-6">
                <Trophy className="w-5 h-5 text-teal-400" />
                <span className="text-teal-300 font-medium">Our Achievements</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Awards & <span className="text-teal-400">Recognition</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                Celebrating excellence in EPC contracting and our commitment to delivering 
                outstanding results for our partners and clients.
              </p>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent" />
        </section>

        {/* Awards Section */}
        <section className="py-20 -mt-12 relative z-10">
          <div className="max-w-7xl mx-auto px-4">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 text-teal-600 mb-4">
                <Award className="w-5 h-5" />
                <span className="font-semibold uppercase tracking-wider text-sm">Recognition</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
                Industry Recognition
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Our dedication to quality and partnership excellence has been recognized by leading organizations in the industry.
              </p>
            </div>

            {/* Awards Grid */}
            <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
              {awards.map((award) => (
                <div 
                  key={award.id}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100 hover:shadow-2xl transition-all duration-500 group"
                >
                  {/* Award Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100">
                    <img 
                      src={award.image} 
                      alt={award.title}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-teal-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                        {award.category}
                      </span>
                    </div>
                    {/* Year Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        {award.year}
                      </span>
                    </div>
                  </div>

                  {/* Award Details */}
                  <div className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Trophy className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">
                          {award.title}
                        </h3>
                        <p className="text-teal-600 font-semibold text-lg mb-1">
                          {award.organization}
                        </p>
                        <p className="text-slate-500 text-sm mb-4">
                          {award.event}
                        </p>
                        <p className="text-slate-600 leading-relaxed">
                          {award.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* More Awards Coming Soon */}
            <div className="mt-16 text-center">
              <div className="inline-flex items-center gap-3 bg-slate-100 rounded-full px-6 py-3">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                    <Award className="w-4 h-4 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <Star className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-slate-600 font-medium">More achievements coming soon</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Partner With an Award-Winning EPC Contractor
            </h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Join our growing list of satisfied partners. Let's build something remarkable together.
            </p>
            <a 
              href="/contact"
              className="inline-flex items-center gap-2 bg-white text-teal-600 font-semibold px-8 py-4 rounded-full hover:bg-teal-50 transition-colors shadow-lg hover:shadow-xl"
            >
              Discuss Your Project
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};
